import React, { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { istDate } from "@/components/DispatchDatePicker";
import { KIND_LABEL } from "@/lib/payoutExport";
import { inr } from "./PayoutBits";

const inp = "w-full border border-[#DCDCD3] rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#385C4B]";
export const Field = ({ label, children }) => (
  <label className="block space-y-1">
    <span className="text-[10px] font-mono uppercase tracking-wider text-[#6B6B61]">{label}</span>
    {children}
  </label>
);

export function Modal({ title, onClose, children, size = "max-w-md", testid }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${size} w-[calc(100vw-2rem)] grid-cols-1 max-h-[90vh] overflow-y-auto`} data-testid={testid}>
        <DialogHeader className="min-w-0 pr-6"><DialogTitle className="truncate">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3 min-w-0">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

export const SaveBtn = ({ busy, onClick, label = "Save", testid }) => (
  <button disabled={busy} onClick={onClick} data-testid={testid}
    className="w-full bg-[#385C4B] text-white rounded-md py-2.5 text-sm font-semibold hover:bg-[#2c4a3c] disabled:opacity-50">
    {busy ? "Saving…" : label}
  </button>
);

export function useSubmit(fn, onDone) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await fn(); onDone(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return [busy, run];
}

export function RateFields({ kind, v, set }) {
  const n = (k) => (
    <input type="number" min="0" step="0.01" value={v[k]} onChange={(e) => set({ ...v, [k]: e.target.value })} className={inp} data-testid={`payout-rate-${k}`} />
  );
  return (
    <div className={`grid gap-2 ${kind === "developer" ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2"}`}>
      {kind === "developer" ? (
        <>
          <Field label="Indane ₹">{n("indane")}</Field>
          <Field label="Bharat ₹">{n("bharat")}</Field>
          <Field label="HP ₹">{n("hp")}</Field>
        </>
      ) : <Field label="Rate ₹ / booking">{n("rate")}</Field>}
      <Field label="Effective from">
        <input type="date" value={v.from_date} onChange={(e) => set({ ...v, from_date: e.target.value })} className={inp} data-testid="payout-rate-from" />
      </Field>
    </div>
  );
}
export const emptyRate = () => ({ from_date: istDate(0), rate: "", indane: "", bharat: "", hp: "" });
const rateBody = (v) => ({ from_date: v.from_date, rate: +v.rate || 0, indane: +v.indane || 0, bharat: +v.bharat || 0, hp: +v.hp || 0 });

export function AgencyPicker({ value, onChange, selfId }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/payouts/agencies").then(({ data }) => setList(data.agencies)).catch((e) => toast.error(formatApiError(e))); }, []);
  const shown = useMemo(() => (list || []).filter((a) => a.name.toLowerCase().includes(q.trim().toLowerCase())), [list, q]);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const free = shown.filter((a) => !(a.taken_by && a.taken_by.person_id !== selfId)).map((a) => a.id);
  const selectAll = () => onChange([...new Set([...value, ...free])]);
  const clearAll = () => onChange(value.filter((id) => !free.includes(id)));
  if (!list) return <div className="text-xs text-[#6B6B61]">Loading agencies…</div>;
  return (
    <div className="space-y-2" data-testid="payout-agency-picker">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#6B6B61]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search agency…" className={`${inp} pl-7`} data-testid="payout-agency-search" />
      </div>
      <div className="flex items-center gap-2 text-[11px] text-[#6B6B61]">
        <span className="flex-1" data-testid="payout-agency-count">{value.length} selected</span>
        <button type="button" onClick={selectAll} className="px-2.5 py-1 rounded border border-[#385C4B] text-[#385C4B] hover:bg-[#385C4B]/5" data-testid="payout-agency-select-all">Select All</button>
        <button type="button" onClick={clearAll} className="px-2.5 py-1 rounded border border-[#C8553D] text-[#C8553D] hover:bg-red-50" data-testid="payout-agency-clear-all">Clear All</button>
      </div>
      <div className="max-h-56 overflow-y-auto border border-[#EDEDE5] rounded divide-y divide-[#F0F0EA]">
        {shown.map((a) => {
          const other = a.taken_by && a.taken_by.person_id !== selfId;
          return (
            <label key={a.id} className={`flex items-center gap-2 px-2.5 py-1.5 text-sm min-w-0 ${other ? "opacity-50" : "cursor-pointer hover:bg-[#F5F5F0]"}`}>
              <input type="checkbox" disabled={other} checked={value.includes(a.id)} onChange={() => toggle(a.id)} data-testid={`payout-agency-${a.id}`} />
              <span className="truncate flex-1">{a.name}</span>
              {other && <span className="text-[10px] font-mono shrink-0">{a.taken_by.name}</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function NamePicker({ list, chosen, onPick }) {
  const [q, setQ] = useState("");
  const hits = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? list.filter((c) => `${c.name} ${c.username}`.toLowerCase().includes(t)).slice(0, 8) : [];
  }, [q, list]);
  if (chosen) {
    return (
      <div className="flex items-center gap-2 border border-[#385C4B] bg-[#385C4B]/5 rounded-md px-3 py-2 text-sm min-w-0" data-testid="payout-person-chosen">
        <span className="truncate flex-1">Selected: <b>{chosen.name}</b></span>
        <button onClick={() => { onPick(null); setQ(""); }} className="shrink-0 text-xs text-[#C8553D] flex items-center gap-1" data-testid="payout-person-change">
          <X className="w-3.5 h-3.5" />Change
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-[#6B6B61]" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name…" className={`${inp} pl-8`} data-testid="payout-person-search" />
      </div>
      {q.trim() && (
        <div className="border border-[#EDEDE5] rounded-md divide-y divide-[#F0F0EA] bg-white" data-testid="payout-person-results">
          {hits.length === 0 ? <div className="px-3 py-2 text-xs text-[#6B6B61]">No match found</div> : hits.map((c, i) => (
            <div key={`${c.name}-${c.user_id}`} className="flex items-center gap-2 px-3 py-1.5 text-sm min-w-0">
              <span className="truncate flex-1">{c.name}</span>
              <button onClick={() => onPick(c)} className="shrink-0 text-xs font-semibold px-2.5 py-1 rounded bg-[#385C4B] text-white hover:bg-[#2c4a3c]" data-testid={`payout-person-pick-${i}`}>Select</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PersonForm({ kind, onClose, onSaved }) {
  const [chosen, setChosen] = useState(null);
  const [rate, setRate] = useState(emptyRate());
  const [agencies, setAgencies] = useState([]);
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/payouts/candidates", { params: { kind } }).then(({ data }) => setList(data.candidates)).catch((e) => toast.error(formatApiError(e)));
  }, [kind]);
  const [busy, save] = useSubmit(async () => {
    if (!chosen) throw new Error("Select a name from the list first");
    await api.post("/payouts/people", { kind, name: chosen.name, user_id: chosen.user_id, agency_ids: agencies, rate: kind === "booker" ? null : rateBody(rate) });
    toast.success(`${chosen.name} added`);
  }, onSaved);
  if (list && !list.length) {
    return (
      <Modal title={`+ ${KIND_LABEL[kind]}`} onClose={onClose} testid="payout-person-form">
        <div className="text-sm text-[#6B6B61]" data-testid="payout-person-none">All users are already added. Create a new user with the {KIND_LABEL[kind]} role in Admin → Users.</div>
      </Modal>
    );
  }
  return (
    <Modal title={`+ ${KIND_LABEL[kind]}`} onClose={onClose} size={kind === "salesman" ? "max-w-2xl" : undefined} testid="payout-person-form">
      <div className="space-y-1">
        <span className="text-[10px] font-mono uppercase tracking-wider text-[#6B6B61]">{KIND_LABEL[kind]} name (search and select)</span>
        {list ? <NamePicker list={list} chosen={chosen} onPick={setChosen} /> : <div className="text-xs text-[#6B6B61]">Loading…</div>}
      </div>
      <div className="text-[11px] text-[#6B6B61]">Logins are created only in Admin → Users. A user with the same name is linked automatically.</div>
      {kind !== "booker" && <RateFields kind={kind} v={rate} set={setRate} />}
      {kind === "salesman" && <AgencyPicker value={agencies} onChange={setAgencies} />}
      <SaveBtn busy={busy} onClick={save} testid="payout-person-save" />
    </Modal>
  );
}

export function RateForm({ person, onClose, onSaved }) {
  const [rate, setRate] = useState(emptyRate());
  const [busy, save] = useSubmit(async () => {
    await api.post(`/payouts/people/${person.id}/rates`, rateBody(rate));
    toast.success("Rate saved");
  }, onSaved);
  return (
    <Modal title={`${person.name} · Change Rate`} onClose={onClose} testid="payout-rate-form">
      <RateFields kind={person.kind} v={rate} set={setRate} />
      <div className="text-[11px] text-[#6B6B61]">The new rate applies to bookings from this date. Earlier records stay unchanged.</div>
      <SaveBtn busy={busy} onClick={save} testid="payout-rate-save" />
    </Modal>
  );
}

export function AgenciesForm({ person, onClose, onSaved }) {
  const [ids, setIds] = useState(person.agency_ids || []);
  const [busy, save] = useSubmit(async () => {
    await api.patch(`/payouts/people/${person.id}`, { agency_ids: ids });
    toast.success("Agencies updated");
  }, onSaved);
  return (
    <Modal title={`${person.name} · Agencies`} onClose={onClose} size="max-w-2xl" testid="payout-agencies-form">
      <AgencyPicker value={ids} onChange={setIds} selfId={person.id} />
      <SaveBtn busy={busy} onClick={save} testid="payout-agencies-save" />
    </Modal>
  );
}

const ENTRY_TITLE = { work: "+ Work Entry", plus: "+ Bonus / Extra", payment: "+ Payment", minus: "− Deduction" };
export function EntryForm({ person, kind, entry, onClose, onSaved }) {
  const [f, setF] = useState({ date: entry?.date || istDate(0), qty: entry?.qty ?? "", rate: entry?.rate ?? "", amount: entry?.amount ?? "", note: entry?.note || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [busy, save] = useSubmit(async () => {
    const body = { kind, date: f.date, qty: +f.qty || 0, rate: +f.rate || 0, amount: +f.amount || 0, mode: "", note: f.note };
    if (entry) await api.patch(`/payouts/entries/${entry.id}`, body);
    else await api.post(`/payouts/people/${person.id}/entries`, body);
    toast.success("Entry saved");
  }, onSaved);
  return (
    <Modal title={`${person.name} · ${ENTRY_TITLE[kind]}`} onClose={onClose} testid="payout-entry-form">
      <Field label="Date"><input type="date" value={f.date} min={person.opening_balance?.date} max={istDate(0)} onChange={set("date")} className={inp} data-testid="payout-entry-date" /></Field>
      {kind === "work" ? (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Quantity"><input type="number" min="0" value={f.qty} onChange={set("qty")} className={inp} data-testid="payout-entry-qty" /></Field>
          <Field label="Rate ₹"><input type="number" min="0" step="0.01" value={f.rate} onChange={set("rate")} className={inp} data-testid="payout-entry-rate" /></Field>
          <div className="col-span-2 text-sm" data-testid="payout-entry-amount-preview">Amount: <b>{inr((+f.qty || 0) * (+f.rate || 0))}</b></div>
        </div>
      ) : (
        <Field label="Amount ₹"><input type="number" min="0" step="0.01" value={f.amount} onChange={set("amount")} className={inp} data-testid="payout-entry-amount" /></Field>
      )}
      <Field label={kind === "minus" ? "Reason" : "Note"}><input value={f.note} onChange={set("note")} className={inp} data-testid="payout-entry-note" /></Field>
      <SaveBtn busy={busy} onClick={save} testid="payout-entry-save" />
    </Modal>
  );
}
